import classNames from "classnames";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ReactNode, useEffect } from "react";
import { isRichTextEmpty, sanitizeRichText } from "../../utils/sanitizeHtml";
import { ErrorText } from "../typography/errorText";

type RichTextEditorProps = {
  name: string;
  value?: string;
  placeholder?: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  title?: string;
  hasTitle?: boolean;
  errors?: string;
  disabled?: boolean;
  containerClassName?: string;
};

/* ---- Inline toolbar icons (sprite only has formatting-agnostic icons) ---- */

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const BoldIcon = () => (
  <svg {...iconProps}>
    <path d="M6 4h8a4 4 0 0 1 0 8H6zM6 12h9a4 4 0 0 1 0 8H6z" />
  </svg>
);
const ItalicIcon = () => (
  <svg {...iconProps}>
    <line x1="19" y1="4" x2="10" y2="4" />
    <line x1="14" y1="20" x2="5" y2="20" />
    <line x1="15" y1="4" x2="9" y2="20" />
  </svg>
);
const UnderlineIcon = () => (
  <svg {...iconProps}>
    <path d="M6 3v7a6 6 0 0 0 12 0V3" />
    <line x1="4" y1="21" x2="20" y2="21" />
  </svg>
);
const StrikeIcon = () => (
  <svg {...iconProps}>
    <line x1="4" y1="12" x2="20" y2="12" />
    <path d="M17.5 6.5A4.5 4.5 0 0 0 13 4h-2a3.5 3.5 0 0 0-1.6 6.6" />
    <path d="M7 17a3.5 3.5 0 0 0 3.5 2.5h2A4 4 0 0 0 16 16.5" />
  </svg>
);
const BulletListIcon = () => (
  <svg {...iconProps}>
    <line x1="9" y1="6" x2="20" y2="6" />
    <line x1="9" y1="12" x2="20" y2="12" />
    <line x1="9" y1="18" x2="20" y2="18" />
    <circle cx="4" cy="6" r="1" fill="currentColor" />
    <circle cx="4" cy="12" r="1" fill="currentColor" />
    <circle cx="4" cy="18" r="1" fill="currentColor" />
  </svg>
);
const OrderedListIcon = () => (
  <svg {...iconProps}>
    <line x1="10" y1="6" x2="20" y2="6" />
    <line x1="10" y1="12" x2="20" y2="12" />
    <line x1="10" y1="18" x2="20" y2="18" />
    <path d="M4 6h1v4M4 10h2" strokeWidth={1.5} />
    <path d="M4 14h2v1l-2 2v1h2" strokeWidth={1.5} />
  </svg>
);
const ToolbarButton = ({
  onClick,
  active,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    aria-pressed={active}
    disabled={disabled}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={classNames(
      "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
      "text-grey-700 hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-40",
      { "!bg-grey-950 !text-white hover:!bg-grey-950": active },
    )}
  >
    {children}
  </button>
);

const Toolbar = ({ editor }: { editor: Editor }) => {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-grey-100 px-2 py-1.5">
      <ToolbarButton
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <StrikeIcon />
      </ToolbarButton>

      <span className="mx-1 h-5 w-px bg-grey-100" />

      <ToolbarButton
        label="Bulleted list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <BulletListIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <OrderedListIcon />
      </ToolbarButton>
    </div>
  );
};

export const RichTextEditor = ({
  name,
  value = "",
  placeholder = "Enter description",
  onChange,
  onBlur,
  title,
  hasTitle = false,
  errors = "",
  disabled = false,
  containerClassName = "",
}: RichTextEditorProps) => {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        // Only the formats in the acceptance criteria are allowed; disabling
        // these removes them from the schema so pasted content is stripped.
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
      }),
      Underline,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "tiptap-content rich-text min-h-[120px] w-full px-3 py-3 text-sm font-normal text-grey-950 focus:outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(isRichTextEmpty(html) ? "" : sanitizeRichText(html));
    },
    onBlur: () => onBlur?.(),
  });

  // Keep the editor in sync when the value is set externally (e.g. when the
  // edit form loads an existing event after mount).
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    const current = editor.getHTML();
    const bothEmpty = isRichTextEmpty(incoming) && isRichTextEmpty(current);
    if (!bothEmpty && incoming !== current) {
      editor.commands.setContent(incoming, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className={containerClassName}>
      {hasTitle && title && (
        <div className="mb-1 text-xs leading-4 text-grey-700">{title}</div>
      )}
      <div
        className={classNames(
          "w-full overflow-hidden rounded-lg border border-grey-100 bg-grey-50 transition-all",
          "focus-within:border-[2px] focus-within:border-grey-950",
          {
            "focus-within:!border-danger": !!errors,
            "!bg-grey-100": disabled,
          },
        )}
      >
        {editor && <Toolbar editor={editor} />}
        <EditorContent editor={editor} />
      </div>
      {errors && <ErrorText text={errors} />}
    </div>
  );
};
